import { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Field } from 'react-final-form';
import Dropdown, { DropdownOption } from 'src/common/components/DropdownDS22';
import Label from 'src/common/components/Label';
import type { Occasion } from 'src/common/types/api/v4/trips/occasions';
import styles from './OccasionField.module.scss';
import InputWrapper from '../InputWrapper';

type Props = {
  name: string;
  showErrorOnTouch?: boolean;
  /** False if your form implement Form level validation onSubmit */
  fieldLevelValidation?: boolean;
  /** Occassions from `serverGetOccasions` */
  occasions: Occasion[];
};

/**
 * Occassion values originate from serverGetOccasions
 * Backend expects the selected value as a single [number] wrapped in array
 */
const OccasionField: FC<Props> = ({
  name,
  showErrorOnTouch = true,
  fieldLevelValidation = true,
  occasions,
}) => {
  const [t] = useTranslation('common');
  const options: DropdownOption<string>[] = useMemo(
    () => Object.values(occasions).map((occasion) => ({
      // `id` is stored as string in form state but converted to number using OCCASIONS mapping
      // when posting to API. For example, "0" string maps to OCCASIONS.BIRTHDAY (0) number value
      id: `${occasion.value}`,
      dropdownLabel: occasion.label,
    })),
    [occasions],
  );

  const validateOccasion = (value: string | undefined) => {
    if (!fieldLevelValidation) { return undefined; }
    if (!value) {
      return t('occasion-error');
    }
    return undefined;
  };

  return (
    <div className={styles.root}>
      <InputWrapper name={name} showErrorOnTouch={showErrorOnTouch}>
        <Label
          htmlFor={name}
          label={t('select-occasion--label')}
          size="m"
        />
        <Field
          name={name}
          validate={validateOccasion}
        >
          {({ input: { onChange, value }, meta }) => {
            const isTouchedOrSubmitted = meta.touched || meta.submitFailed;
            const hasError = meta.error && isTouchedOrSubmitted;
            const selectedValue = options.find(({ id }) => id === value) || null;

            return (
              <Dropdown
                selectedValue={selectedValue}
                options={options}
                onChange={(option) => onChange(option.id)}
                variant="white"
                instructionLabel={t('select-occasion--default')}
                fullWidth
                buttonSize="l"
                optionSize="l"
                error={hasError}
              />
            );
          }}
        </Field>
      </InputWrapper>
    </div>
  );
};

export default OccasionField;
